import { PxToRem, PX_TO_REM, SIZES } from '../sizes/sizes';
import { AllowableGutter, computeSpace, IGutterProps } from '../spaces/space';
import { css } from 'styled-components';

export const MEDIA_QUERY = {
  MIN: 'min',
  MAX: 'max'
} as const;

export type MediaQueryType = typeof MEDIA_QUERY.MIN | typeof MEDIA_QUERY.MAX;

export type BreakpointSize =
  | typeof SIZES.XX_SMALL
  | typeof SIZES.X_SMALL
  | typeof SIZES.SMALL
  | typeof SIZES.MEDIUM
  | typeof SIZES.LARGE
  | typeof SIZES.X_LARGE
  | typeof SIZES.XX_LARGE
  | typeof SIZES.XXX_LARGE;

// Helper for responsive to map current container width to corresponding Sizes
export const breakpointsWidthHelper: Record<BreakpointSize, number> = {
  [SIZES.XX_SMALL]: 0,
  [SIZES.X_SMALL]: 528,
  [SIZES.SMALL]: 576,
  [SIZES.MEDIUM]: 768,
  [SIZES.LARGE]: 992,
  [SIZES.X_LARGE]: 1200,
  [SIZES.XX_LARGE]: 1440,
  [SIZES.XXX_LARGE]: 1920
};

export const breakpoints: Record<BreakpointSize, '0' | PxToRem> = {
  [SIZES.XX_SMALL]: '0',
  [SIZES.X_SMALL]: PX_TO_REM[528],
  [SIZES.SMALL]: PX_TO_REM[576],
  [SIZES.MEDIUM]: PX_TO_REM[768],
  [SIZES.LARGE]: PX_TO_REM[992],
  [SIZES.X_LARGE]: PX_TO_REM[1200],
  [SIZES.XX_LARGE]: PX_TO_REM[1440],
  [SIZES.XXX_LARGE]: PX_TO_REM[1920]
};

export const breakpointGutters: Record<BreakpointSize, AllowableGutter> = {
  [SIZES.XX_SMALL]: 16,
  [SIZES.X_SMALL]: 16,
  [SIZES.SMALL]: 16,
  [SIZES.MEDIUM]: 32,
  [SIZES.LARGE]: 32,
  [SIZES.X_LARGE]: 32,
  [SIZES.XX_LARGE]: 32,
  [SIZES.XXX_LARGE]: 32
};

export const mediaQueryBreakpoints = [SIZES.XX_SMALL, SIZES.X_SMALL, SIZES.SMALL, SIZES.MEDIUM, SIZES.LARGE, SIZES.X_LARGE, SIZES.XX_LARGE, SIZES.XXX_LARGE];

export const generateMediaQueriesByBreakpoints = (queryType: MediaQueryType) =>
  mediaQueryBreakpoints.reduce(
    (accumulator: Record<BreakpointSize, string>, breakpoint: BreakpointSize) => {
      accumulator[breakpoint] = `@media only screen and (${queryType}-width: ${breakpoints[breakpoint]})`;
      return accumulator;
    },
    {} as Record<BreakpointSize, string>
  );

export const mediaQueries: Record<BreakpointSize, string> & Record<Exclude<MediaQueryType, `default`>, Record<BreakpointSize, string>> = {
  // TODO: remove this once all the dependencies are updated with (min/max) structure.
  // All new places should use min/max structure e.g.
  // Do not use theme.mediaQueries[SIZES.SMALL]
  // Use theme.mediaQueries[MEDIA_QUERY.MIN/MEDIA_QUERY.MAX][SIZES.SMALL]
  ...generateMediaQueriesByBreakpoints(MEDIA_QUERY.MIN),
  [MEDIA_QUERY.MIN]: {
    ...generateMediaQueriesByBreakpoints(MEDIA_QUERY.MIN)
  },
  [MEDIA_QUERY.MAX]: {
    ...generateMediaQueriesByBreakpoints(MEDIA_QUERY.MAX)
  }
};

export interface IFullWidthProps {
  fullWidth?: boolean | BreakpointSize;
}

export const useFullWidthOnSmallCss = css<IFullWidthProps>`
  width: 100%;

  ${({ theme, fullWidth }) =>
    !fullWidth &&
    css`
      ${theme.mediaQueries[MEDIA_QUERY.MIN][SIZES.SMALL]} {
        width: auto;
      }
    `};
`;

export const useFullWidthCss = css<IFullWidthProps>`
  ${({ fullWidth, theme }) =>
    fullWidth &&
    (fullWidth === true
      ? css`
          width: 100%;
        `
      : css`
          ${theme.mediaQueries[MEDIA_QUERY.MAX][fullWidth]} {
            width: 100%;
          }
        `)};
`;

export const useFullWidthWithGuttersCss = css<IFullWidthProps & IGutterProps>`
  ${({ fullWidth, gutter, gutterLeft, gutterRight }) => {
    const extraLeftSpace = gutter || gutterLeft ? ` - ${computeSpace(gutterLeft, gutter)}` : '';
    const extraRightSpace = gutter || gutterRight ? ` - ${computeSpace(gutterRight, gutter)}` : '';
    return (
      fullWidth &&
      css`
        width: ${extraLeftSpace || extraRightSpace ? `calc(100%${extraLeftSpace}${extraRightSpace})` : `100%`};
      `
    );
  }};
`;
